import { useRef, useState } from 'react'
import {
  currentMonthKey,
  isUserDateInputValid,
  USER_DATE_INPUT_MAX,
  USER_DATE_INPUT_MIN,
  USER_DATE_YEAR_MAX,
  USER_DATE_YEAR_MIN,
  USER_DATE_YEAR_RANGE_LABEL,
  type PlanningPeriodKey,
} from '@crm/shared'
import { ModalShell } from './ModalShell'
import { RatingReportSection } from '../RatingReportSection'

/**
 * Модалка экспорта отчёта по рейтингу план/факт. Селектор периода
 * (Месяц/Квартал/Год/Период…) + кнопка «Скачать отчёт» → PDF (A4 portrait)
 * с двумя блоками: «Рейтинг план/факт» и «Рейтинг АКБ план/факт».
 *
 * Рендерим контент отчёта в скрытом off-screen блоке, ждём загрузки данных
 * (react-query тянет /planning/rating с явными параметрами периода) и
 * снимаем через html2canvas → jsPDF. PDF-библиотеки подгружаются ленивым
 * импортом, чтобы не раздувать основной бандл.
 */

export interface ExportReportModalProps {
  /** Дефолт периода (берётся из шапки дашборда). day/week дашборда мапим в month. */
  defaultPeriodKey?: PlanningPeriodKey
  defaultFrom?: string | null
  defaultTo?: string | null
  onClose: () => void
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function defaultCustomRange(): { from: string; to: string } {
  const now = new Date()
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  return {
    from: `${y}-${pad2(m + 1)}-01`,
    to: `${y}-${pad2(m + 1)}-${pad2(last)}`,
  }
}

const RU_MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
]

function periodHumanLabel(
  key: PlanningPeriodKey,
  date: string | null,
  from: string | null,
  to: string | null,
): string {
  if (key === 'month' && date) {
    const m = parseInt(date.slice(5, 7), 10)
    return `${RU_MONTHS[m - 1]} ${date.slice(0, 4)}`
  }
  if (key === 'quarter' && date) {
    const m = parseInt(date.slice(5, 7), 10)
    const q = Math.floor((m - 1) / 3) + 1
    return `${q} квартал ${date.slice(0, 4)}`
  }
  if (key === 'year' && date) return `${date} год`
  if (key === 'custom' && from && to) return `${from} — ${to}`
  return ''
}

function periodFileSlug(
  key: PlanningPeriodKey,
  date: string | null,
  from: string | null,
  to: string | null,
): string {
  if (key === 'custom' && from && to) return `${from}_${to}`
  if (date) return date
  return new Date().toISOString().slice(0, 10)
}

export function ExportReportModal({
  defaultPeriodKey,
  defaultFrom,
  defaultTo,
  onClose,
}: ExportReportModalProps) {
  const initialKey: PlanningPeriodKey =
    defaultPeriodKey === 'custom' || defaultPeriodKey === 'year' || defaultPeriodKey === 'quarter'
      ? defaultPeriodKey
      : 'month'
  const [key, setKey] = useState<PlanningPeriodKey>(initialKey)
  const [date, setDate] = useState<string>(() => {
    if (initialKey === 'year') return String(new Date().getUTCFullYear())
    return currentMonthKey()
  })
  const def = defaultCustomRange()
  const [from, setFrom] = useState<string>(defaultFrom?.slice(0, 10) ?? def.from)
  const [to, setTo] = useState<string>(defaultTo?.slice(0, 10) ?? def.to)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const captureRef = useRef<HTMLDivElement>(null)

  const handleSetKey = (k: PlanningPeriodKey) => {
    setKey(k)
    if (k === 'year') setDate(String(new Date().getUTCFullYear()))
    else if (k === 'month' || k === 'quarter') setDate(currentMonthKey())
  }

  const onDownload = async () => {
    if (generating) return
    setError(null)
    // Нативный date-input позволяет год из 5-6 цифр (62026) — сервер такое
    // отвергает 422; ловим до генерации, чтобы пользователь сразу увидел где ошибка.
    if (key === 'custom' && (!isUserDateInputValid(from) || !isUserDateInputValid(to))) {
      setError(
        `Даты периода вне допустимого диапазона (${USER_DATE_YEAR_RANGE_LABEL}) — проверьте год`,
      )
      return
    }
    setGenerating(true)
    try {
      // Даём react-query прогрузить /planning/rating в off-screen блоке.
      // При первом открытии запрос летит при mount модалки; при смене периода
      // на лету — тоже. 800мс хватает на тестовой выборке 17 строк.
      await new Promise((r) => setTimeout(r, 800))
      const node = captureRef.current
      if (!node) throw new Error('Контент отчёта не отрисован')

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ])

      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        // Исходный блок прозрачный (opacity:0), чтобы пользователь его не
        // видел. html2canvas клонирует DOM в скрытый iframe — в клоне
        // включаем opacity:1, иначе в canvas попадал прозрачный «белый лист».
        onclone: (_doc, clonedNode) => {
          if (clonedNode instanceof HTMLElement) {
            clonedNode.style.opacity = '1'
          }
        },
      })
      const imgData = canvas.toDataURL('image/png')

      // A4 portrait 210×297мм, поля 8мм. Если контент выше страницы — режем
      // изображение по высоте и распихиваем по нескольким страницам.
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pageWidth = 210
      const pageHeight = 297
      const margin = 8
      const imgWidth = pageWidth - margin * 2
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      if (imgHeight <= pageHeight - margin * 2) {
        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight)
      } else {
        // Разрезаем длинный canvas на куски высотой страницы.
        const pageContentHeight = pageHeight - margin * 2
        const sliceCanvasHeightPx = Math.floor((pageContentHeight * canvas.width) / imgWidth)
        let offsetPx = 0
        let first = true
        while (offsetPx < canvas.height) {
          const sliceHeight = Math.min(sliceCanvasHeightPx, canvas.height - offsetPx)
          const sliceCanvas = document.createElement('canvas')
          sliceCanvas.width = canvas.width
          sliceCanvas.height = sliceHeight
          const ctx = sliceCanvas.getContext('2d')
          if (!ctx) break
          ctx.drawImage(
            canvas,
            0,
            offsetPx,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight,
          )
          const sliceData = sliceCanvas.toDataURL('image/png')
          const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width
          if (!first) pdf.addPage()
          pdf.addImage(sliceData, 'PNG', margin, margin, imgWidth, sliceImgHeight)
          first = false
          offsetPx += sliceHeight
        }
      }

      pdf.save(`rating_${periodFileSlug(key, date, from, to)}.pdf`)
      onClose()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось сгенерировать PDF'
      setError(msg)
    } finally {
      setGenerating(false)
    }
  }

  const humanLabel = periodHumanLabel(key, date, from, to)

  return (
    <ModalShell
      title="Экспорт отчёта"
      subtitle="Рейтинг план / факт и АКБ"
      onClose={onClose}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">Период</div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
              {(
                [
                  { k: 'month' as const, label: 'Месяц' },
                  { k: 'quarter' as const, label: 'Квартал' },
                  { k: 'year' as const, label: 'Год' },
                ] satisfies { k: PlanningPeriodKey; label: string }[]
              ).map((p) => (
                <button
                  key={p.k}
                  onClick={() => handleSetKey(p.k)}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    key === p.k ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => handleSetKey('custom')}
                className={`px-3 py-1.5 text-sm transition-colors border-l border-gray-200 ${
                  key === 'custom' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Период…
              </button>
            </div>

            {(key === 'month' || key === 'quarter') && (
              <input
                type="month"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                title={key === 'quarter' ? 'Месяц начала квартала' : 'Месяц'}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
              />
            )}
            {key === 'year' && (
              <input
                type="number"
                min={USER_DATE_YEAR_MIN}
                max={USER_DATE_YEAR_MAX}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-24"
              />
            )}
            {key === 'custom' && (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={from}
                  min={USER_DATE_INPUT_MIN}
                  max={USER_DATE_INPUT_MAX}
                  onChange={(e) => setFrom(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                />
                <span className="text-gray-400 text-sm">—</span>
                <input
                  type="date"
                  value={to}
                  min={USER_DATE_INPUT_MIN}
                  max={USER_DATE_INPUT_MAX}
                  onChange={(e) => setTo(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                />
              </div>
            )}
          </div>
          {humanLabel && <div className="text-xs text-gray-400 mt-1.5">Выбрано: {humanLabel}</div>}
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={generating}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={onDownload}
            disabled={generating}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400"
          >
            {generating ? 'Генерируем…' : 'Скачать отчёт'}
          </button>
        </div>
      </div>

      {/* Off-screen контент для html2canvas. Лежит в позиции (0,0) и спрятан
          через opacity:0 + pointer-events:none + z-index:-1. html2canvas
          по-прежнему рендерит элемент по его getBoundingClientRect — с
          реальными координатами {top:0,left:0,width:900}, поэтому canvas
          получается полным. Раньше блок был на left:-10000 — html2canvas
          снимал document и в видимый кадр попадали только торчащие справа
          края баров, имена и план/факт уезжали за -10000. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: 900,
          background: '#ffffff',
          padding: 24,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
        ref={captureRef}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
            Отчёт по рейтингу менеджеров
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Период: {humanLabel || '—'} · Сформировано {new Date().toLocaleString('ru-RU')}
          </div>
        </div>

        <RatingReportSection
          period={key}
          date={key === 'custom' ? undefined : date}
          from={key === 'custom' ? from : undefined}
          to={key === 'custom' ? to : undefined}
          kind="financial"
        />
        <RatingReportSection
          period={key}
          date={key === 'custom' ? undefined : date}
          from={key === 'custom' ? from : undefined}
          to={key === 'custom' ? to : undefined}
          kind="akb"
        />
      </div>
    </ModalShell>
  )
}
